import common from '../common';

export default class tile {
	/**
     * @param {Object} dimensions The dimensions of the tile
     * @param {THREE.Vector2} pos The position of the tile in 2D space. Vector2.x = World.x, Vector2.y = World.z
     * @param {Grid} grid	The parent grid that this tile belongs to.
	 */
	constructor(dimensions = {width: 0, height: 0}, pos = new THREE.Vector2(0, 0), grid) {
		/**
         * @type {Object}   The width and height of this tile. Width is X, Height is Z.
         * @private
		 */
		this._dimensions = dimensions;

		/**
         * @type {Array}    Array containing THREE.Object3D that are inside this tile.
         * @private
		 */
		this._objects = [];

		/**
         * @type {THREE.Vector2}    The position of this tile inside the grid.
         * @private
		 */
		this._gridPos = pos;

		/**
         * @type {Boolean}  Whether or not to draw debug lines.
         * @private
		 */
		this._drawDebug = false;

		/**
         * @type {Grid}
         * @private
		 */
		this._grid = grid;

		/**
         * @type {THREE.Vector3}
         * @private
		 */
		this._3DPosition = new THREE.Vector3();

		/**
         * @type {Boolean}
         * @private
		 */
		this._isTile = false;

		/**
         * @type {Boolean}
         * @private
		 */
		this._isBad = false;

		/**
         * @type {Boolean}
         * @private
		 */
		this._isGoal = false;

		/**
		 * @type {Boolean}
		 * @private
		 */
		this._isWall = false;

		/**
         * @type {Boolean}
         * @private
		 */
		this._isUnderwater = false;

		/**
         * @type {Array}
         * @private
		 */
		this._types = [];

		/**
		 * @type {Actor}
		 */
		this._activatableObject = null;

		/**
		 * @type {Boolean}
		 */
		this._hasObstacle = false;

		this._init();
	}

	/**
     * @private
	 */
	_init() {
		if (this._drawDebug) {
			const mat = new THREE.LineBasicMaterial({
				color: 0xFFFFFF
			});

			const geometry = new THREE.Geometry();

			geometry.vertices.push(
				new THREE.Vector3(0, 0, 0), // BL
				new THREE.Vector3(0, 0, this._dimensions.height), // TL
				new THREE.Vector3(this._dimensions.width, 0, this._dimensions.height), // TR
				new THREE.Vector3(this._dimensions.width, 0, 0), // BR
				new THREE.Vector3(0, 0, 0) // BL
			);

			const line = new THREE.Line(geometry, mat);

			line.position.set(
				this._gridPos.x * this._dimensions.width,
				0,
				this._gridPos.y * this._dimensions.height);

			common.THREEstage.add(line);
		}
		this._3DPosition.set(
			this._gridPos.x * this._dimensions.width,
			0,
			this._gridPos.y * this._dimensions.height
		);
	}

	/**
     * Adds an object to this tile. The object should be removed from the previous tile beforehand.
     * @param {THREE.Object3D}      obj     The object to add
     * @public
	 */
	addObject(obj) {
		this._objects.push(obj);

		return this;
	}

	/**
     * Checks if an object is inside this tile.
     * @param   {THREE.Object3D}    obj    The object to find.
     * @returns {Boolean}                  Whether or not the object has been found.
     * @public
	 */
	getObject(obj) {
		return this._objects.indexOf(obj) > -1;
	}

	/**
     * Removes an object if found inside this tile.
     * @param   {THREE.Object3D}     obj     The object to remove
     * @returns {Boolean}                    Whether or not the object has been removed.
     * @public
	 */
	removeObject(obj) {
		const index = this._objects.indexOf(obj);

		if (index > -1) {
			this._objects.splice(index, 1);

			return true;
		}

		// eslint-disable-next-line no-console
		console.error(`Attempted to remove object from tile {${this._gridPos.x}, ${this._gridPos.y}} but the object was not found.`);
		common.t.track('game error event', {
			gamelevel     : common.currentLevel,
			gameerror     : 1,
			gameerrorcode : `Attempted to remove object from tile {${this._gridPos.x}, ${this._gridPos.y}} but the object was not found.`,
			gamecharacter : common.player.currentCharacter
		});

		return false;
	}

	destroy() {
		for (const object of this._objects) {
			if (object.isMesh) {
				object.material.dispose();
				object.geometry.dispose();
			}

			if (object.dispose) {
				object.dispose();
			} else if (object.destroy) {
				object.destroy();
			}
		}
	}

	/* GETTERS*/
	get objects() {
		return this._objects;
	}

	get position() {
		return this._gridPos;
	}

	get width() {
		return this._dimensions.width;
	}

	get height() {
		return this._dimensions.height;
	}

	get position3D() {
		return this._3DPosition;
	}

	get isBad() {
		return this._isBad;
	}

	get isTile() {
		return this._isTile;
	}

	get isGoal() {
		return this._isGoal;
	}

	get isUnderwater() {
		return this._isUnderwater;
	}

	get x() {
		return this.position.x;
	}

	get y() {
		return this.position.y;
	}

	get types() {
		return this._types;
	}

	get activatableObject() {
		return this._activatableObject;
	}

	get isWall() {
		return this._isWall;
	}

	get hasObstacle() {
		return this._hasObstacle;
	}

	/* SETTERS*/
	set position3D(v) {
		this._3DPosition.copy(v);
	}

	set isBad(v) {
		this._isBad = v;
	}

	set isTile(v) {
		this._isTile = v;
	}

	set isGoal(v) {
		this._isGoal = v;
	}

	set isUnderwater(v) {
		this._isUnderwater = v;
	}

	set types(v) {
		this._types = v;
	}

	set activatableObject(v) {
		this._activatableObject = v;
	}

	set isWall(v) {
		this._isWall = v;
	}

	set hasObstacle(v) {
		this._hasObstacle = v;
	}
}

tile.SPIKE_TRAP = 'spikes';
tile.CRUSHER_TRAP = 'crusher';
